import { Dropdown } from "flowbite-react";
import {
  AlertTriangle,
  Bell,
  CheckCircle,
  Clock,
  Clock3,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroller";
import ErrorFallback from "../../../components/error-fallback/ErrorFallback";
import { convertDateISO } from "../../../utils";
import {
  useClearAllNotifications,
  useReadUserNotification,
  useUserNotifications,
  GET_NOTIFICATIONS_QUERY_KEY,
  useClearNotification,
} from "../../../api/notification/notifications.hooks";
import { UseMutationResult } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { TApiServiceError } from "../../../api/apiService";
import { TNotificationsResponse } from "../../../api/notification/notifications.types";
import { TNotification } from "../../../api/notification/notifications.types";
import { Typography } from "../../../components/ui";
import KpiLoaders from "../loaders/KpiLoaders";

const Notifications = () => {
  const { t } = useTranslation(["common"]) as { t: (key: string) => string };
  const [currentPage, setCurrentPage] = useState(1);
  const [allNotifications, setAllNotifications] = useState<TNotification[]>([]);
  const [lastTotalCount, setLastTotalCount] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();

  const {
    data: notificationsResponse,
    isLoading: notificationLoader,
    error,
    refetch: refetchNotifications,
  } = useUserNotifications(currentPage, {
    // staleTime: 30000,
    // refetchInterval: 60000,
  });

  const clearAllMutation: UseMutationResult<
    TNotificationsResponse,
    TApiServiceError,
    void,
    unknown
  > = useClearAllNotifications({
    onSuccess: async () => {
      setCurrentPage(1);
      setAllNotifications([]);
      setLastTotalCount(0);
      await queryClient.invalidateQueries({
        queryKey: [GET_NOTIFICATIONS_QUERY_KEY],
      });
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = 0;
      }
    },
    onError: (err: TApiServiceError) => {
      console.error("Error clearing all notifications:", err);
    },
  });

  const readNotificationMutation = useReadUserNotification({
    onSuccess: async () => {
      // Refetch to get updated notification list
      await refetchNotifications();
    },
    onError: (error) => {
      console.error("Error marking notification as read:", error);
    },
  });

  const clearNotificationMutation = useClearNotification({
    onSuccess: async () => {
      // Refetch to get updated notification list
      setCurrentPage(1);
      await refetchNotifications();
    },
    onError: (error) => {
      console.error("Error clearing notification :", error);
    },
  });

  useEffect(() => {
    const newResults = notificationsResponse?.data?.results ?? [];
    const count = notificationsResponse?.data?.count;
    if (typeof count === "number" && count > 0) {
      setLastTotalCount(count);
    }
    if (currentPage === 1) {
      setAllNotifications(newResults);
    } else {
      setAllNotifications((prev) => {
        const existingIds = new Set(prev.map((n) => n.internal_id));
        const filteredNew = newResults.filter(
          (n) => !existingIds.has(n.internal_id),
        );
        return [...prev, ...filteredNew];
      });
    }
  }, [notificationsResponse, currentPage]);

  const totalCount = notificationsResponse?.data?.count ?? lastTotalCount;
  const totalPages = notificationsResponse?.data?.total_pages ?? 1;

  const fetchMoreData = useCallback(() => {
    if (
      !notificationLoader &&
      allNotifications.length < totalCount &&
      currentPage < totalPages
    ) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [
    notificationLoader,
    allNotifications.length,
    totalCount,
    currentPage,
    totalPages,
  ]);

  const handleNotificationClick = async (notification: TNotification) => {
    // Only mark as read if it's not already read
    if (!notification.is_read) {
      // Save scroll position before state update
      const scrollPosition = scrollContainerRef.current?.scrollTop ?? 0;

      try {
        // Call API to mark notification as read
        await readNotificationMutation.mutateAsync(notification.internal_id);

        // Restore scroll position after refetch
        requestAnimationFrame(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = scrollPosition;
          }
        });
      } catch (error) {
        console.error("Error marking notification as read:", error);
      }
    }
  };

  const handleClearNotification = async (
    e: React.MouseEvent,
    notificationId: string,
  ) => {
    e.stopPropagation(); // Prevent triggering the notification click

    // Save scroll position before API call
    const scrollPosition = scrollContainerRef.current?.scrollTop ?? 0;

    try {
      // Call API to clear the notification
      await clearNotificationMutation.mutateAsync(notificationId);

      // Restore scroll position after mutation completes
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollPosition;
        }
      });
    } catch (error) {
      console.error("Error clearing notification:", error);
    }
  };

  const getNotificationIcon = (type: string) => {
    const iconProps = { size: 18 };
    switch (type) {
      case "processing":
        return <Clock3 {...iconProps} className="text-blue" />;
      case "success":
        return <CheckCircle {...iconProps} className="text-green" />;
      case "error":
      case "failed":
        return <X {...iconProps} className="text-destructive" />;
      case "warning":
        return <AlertTriangle {...iconProps} className="text-amber" />;
      case "info":
      default:
        return <Clock {...iconProps} className="text-gray" />;
    }
  };

  const formatDate = (timestamp: string) =>
    timestamp ? convertDateISO(timestamp, "MM/dd/yyyy hh:mm a") : "-";

  const NotificationTrigger = React.forwardRef<HTMLDivElement>((props, ref) => (
    <div ref={ref} {...props} className="relative cursor-pointer">
      <Bell size={22} className="transition-colors hover:text-blue" />
      {totalCount > 0 && (
        <Typography
          variant={"article"}
          className="absolute -right-2 -top-3 flex text-xs min-w-[20px] min-h-[20px] items-center justify-center rounded-full bg-destructive text-white"
        >
          {totalCount > 9 ? "9+" : totalCount}
        </Typography>
      )}
    </div>
  ));
  NotificationTrigger.displayName = "NotificationTrigger";
  const isError = !!error;
  return (
    <Dropdown
      label=""
      renderTrigger={() => <NotificationTrigger />}
      placement="bottom-end"
      className="z-50 w-[350px]"
    >
      <ErrorFallback isError={isError} error={error as Error | undefined}>
        <>
          <div className="flex items-center justify-between px-4 py-2 border-b">
            <Typography affects={"semiBold"}> {t("NOTIFICATIONS")} </Typography>
            <div className="flex items-center gap-2">
              <Typography
                variant={"article"}
                affects={"medium"}
                className="rounded-full bg-destructive px-2 py-1 text-xs text-destructive-foreground"
              >
                {totalCount > 99 ? "99+" : totalCount}
              </Typography>
              <button
                type="button"
                disabled={
                  (clearAllMutation.isPending ?? false) || totalCount === 0
                }
                onClick={() => clearAllMutation.mutate(undefined)}
                className="text-xs cursor-pointer text-blue hover:underline disabled:opacity-50 outline-none focus:outline-none"
                title="Clear all notifications"
              >
                {clearAllMutation.isPending ? "Processing" : "Clear All"}
              </button>
            </div>
          </div>
          {allNotifications.length === 0 && notificationLoader && (
            <div className="grid grid-cols-1 gap-4 p-2">
              <KpiLoaders numberOfLoaders={1} className="h-[100px]" />
            </div>
          )}
          {allNotifications.length === 0 && !notificationLoader && (
            <div className="grid grid-cols-1 gap-4">
              <Typography
                variant="p"
                className="text-muted-foreground m-2 h-[100px] flex items-center justify-center"
              >
                No notifications available.
              </Typography>
            </div>
          )}
          {(readNotificationMutation.isPending ||
            clearAllMutation.isPending ||
            clearNotificationMutation.isPending) && (
            <div className="grid grid-cols-1 gap-4 p-2">
              <KpiLoaders numberOfLoaders={1} className="h-[100px]" />
            </div>
          )}

          {!readNotificationMutation.isPending &&
            !clearAllMutation.isPending &&
            !clearNotificationMutation.isPending && (
              <div
                ref={scrollContainerRef}
                className="max-h-[300px] overflow-y-auto"
              >
                <InfiniteScroll
                  pageStart={0}
                  loadMore={fetchMoreData}
                  hasMore={
                    allNotifications.length < totalCount &&
                    currentPage < totalPages &&
                    !notificationLoader
                  }
                  useWindow={false}
                  loader={
                    <div
                      key="loader"
                      className="py-2 text-center text-sm text-gray"
                    >
                      {t("NOTIFICATIONS_LOADING")}
                    </div>
                  }
                >
                  {allNotifications.map((notification) => {
                    const isRead = notification.is_read;
                    return (
                      <div
                        key={notification.internal_id}
                        className={`relative flex cursor-pointer items-start gap-3 border-b px-4 py-3 transition-all ${
                          isRead
                            ? "bg-white"
                            : "bg-white hover:bg-blue-50 border-l-4 border-l-blue"
                        }`}
                        onClick={() =>
                          void handleNotificationClick(notification)
                        }
                      >
                        <div
                          className={`${isRead ? "opacity-60 mt-1" : "mt-1"}`}
                        >
                          {getNotificationIcon(notification.message_type)}
                        </div>

                        <div className="flex flex-col flex-1 min-w-0">
                          <Typography
                            variant={"p"}
                            affects={isRead ? "default" : "semiBold"}
                            className={
                              isRead ? "text-gray-600" : "text-gray-900"
                            }
                          >
                            {notification.message}
                          </Typography>
                          <Typography
                            variant={"article"}
                            className={`mt-1 text-xs ${isRead ? "text-gray-400" : "text-gray-500"}`}
                          >
                            {formatDate(notification.timestamp)}
                          </Typography>
                        </div>

                        {/* Clear button */}
                        <button
                          onClick={(e) =>
                            void handleClearNotification(
                              e,
                              notification.internal_id,
                            )
                          }
                          className="flex-shrink-0 rounded-full p-1.5 hover:bg-red-100 transition-colors group outline-none focus:outline-none"
                          aria-label="Clear notification"
                          title="Clear notification"
                          id={`clear-notification-${notification.internal_id}-button`}
                        >
                          <X
                            size={16}
                            className="text-gray-400 group-hover:text-red-600 transition-colors"
                          />
                        </button>
                      </div>
                    );
                  })}
                </InfiniteScroll>
              </div>
            )}
        </>
      </ErrorFallback>
    </Dropdown>
  );
};

export default Notifications;
